import { showAppAlert } from '../common/appDialog';
import { generateCountryReport } from '../utils/pdfGenerator';
import { useRef, useEffect, useState, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { db, auth } from '../../firebaseConfig';
import { 
    doc, 
    getDoc,
    collection,
    addDoc,
    serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import useGoogleMaps from './useGoogleMaps';
import { getRiskHexColor } from '../utils/mapUtils';
import { sanitizeFirestoreData } from './reports/ReportShared';
import RiskSidebar from './RiskSidebar';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import TerminologyInline from './TerminologyInline';
import { getDeterministicOffset, normalizeISO3 } from '../utils/geography';
import type { Page } from '@types';
import RiskTrendChart from './analytics/RiskTrendChart';
import RiskTrendIcon from './analytics/RiskTrendIcon';
import { clearRoutePolylines, computeDrivingRoutes, renderRoutePolylines } from '../utils/googleRoutes';
import { getMapCoordinates } from '../utils/mapCoordinates';
import { preserveReportMapPrintView } from '../utils/reportMapPrinting';
import { formatEventConfidenceLevel } from '../utils/eventConfidence';
import { OSINT_EVENT_NOTE } from '../../functions/src/common/riskEventPublication';
import { COUNTRY_PIN_LIMIT, eventCountry, formatEventRiskScore, getEventRiskLabel, getEventRiskScore } from '../utils/riskEventFeed';


export interface UnifiedMapProps {
    isLoaded: boolean;
    mapTitle?: string; 
    riskData?: any[] | null; 
    alerts?: any[]; 
    assets?: any[]; 
    heatmapPoints?: { lat: number; lng: number; weight?: number; radius?: number }[];
    geospatialThreats?: any;
    markers?: any;
    circles?: any;
    threatGroupData?: { lat: number; lng: number; name: string }[]; 
    activeThreatGroup?: { name: string; threatLevel: string; summary: string };
    onAlertClick?: (alert: any) => void;
    onCountryClick?: (iso: string | null) => void; 
    onNavigate?: (nav: { page: Page; params?: any }) => void;
    onOpenGlossary?: (term: string) => void;
    interactive?: boolean;
    initialViewState?: { latitude: number; longitude: number; zoom: number }; 
    theme?: 'light' | 'dark';
    choroplethEnabled?: boolean;
    filterIso3?: string | null;
    highlightIso3?: string | null;
    fitCountryRequest?: number;
    showGlobalAssets?: boolean;
    sidebarTitle?: string;
    /** When set to a category name (not 'All'), only alert markers for that category render.
     *  riskData-derived country/infrastructure markers are suppressed to avoid visual noise. */
    activeCategory?: string;
    focusedLocation?: { lat: number; lng: number; zoom?: number } | null;
    hideSidebar?: boolean;
    countryPinsOnly?: boolean;
    allCountryPins?: boolean;
    readOnly?: boolean;
    weatherMode?: 'historical' | 'current' | 'predictive';
    routeData?: {
        origin: string | { lat: number; lng: number };
        destination: string | { lat: number; lng: number };
        waypoints?: { location: string | { lat: number; lng: number }; stopover: boolean }[];
    } | null;
    tacticalRoutes?: {
        name: string;
        origin?: string;
        destination?: string;
        waypoints: { lat: number; lng: number; description?: string }[];
        color: string;
        isDash?: boolean;
    }[];
}

declare global {
    interface Window {
        google: any;
    }
}

const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
const EMPTY_ARRAY: any[] = [];
const DEFAULT_VIEW_STATE = { latitude: 20, longitude: 0, zoom: 2 };

export const UnifiedMap = forwardRef<any, UnifiedMapProps>(({ 
    isLoaded,           
    riskData = EMPTY_ARRAY,
    markers = EMPTY_ARRAY,       
    circles = EMPTY_ARRAY,
    geospatialThreats = EMPTY_ARRAY,
    choroplethEnabled = false, 
    initialViewState = DEFAULT_VIEW_STATE,
    alerts = EMPTY_ARRAY,
    onCountryClick,
    onNavigate,
    onOpenGlossary,
    activeThreatGroup,
    filterIso3 = 'Global',
    highlightIso3 = null,
    fitCountryRequest = 0,
    showGlobalAssets = true,
    sidebarTitle,
    activeCategory = 'All',
    focusedLocation = null,
    hideSidebar = false,
    countryPinsOnly = false,
    allCountryPins = false,
    readOnly = false,
    weatherMode,
    heatmapPoints = EMPTY_ARRAY,
    assets = EMPTY_ARRAY,
    routeData = null,
    tacticalRoutes = EMPTY_ARRAY,
}, ref) => {
    
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const markerInstancesRef = useRef<any[]>([]);

    
    // ✅ ECC Global Risk Map: Static Coordinate Registry for "Pre-flight" rendering
    // Eliminates geocoding latency for core country indicators
    const COUNTRY_COORDINATES: Record<string, { lat: number, lng: number }> = {
        'AFG': { lat: 33.9391, lng: 67.7100 },
        'ALB': { lat: 41.1533, lng: 20.1683 },
        'DZA': { lat: 28.0339, lng: 1.6596 },
        'AGO': { lat: -11.2027, lng: 17.8739 },
        'ARG': { lat: -38.4161, lng: -63.6167 },
        'ARM': { lat: 40.0691, lng: 45.0382 },
        'AUS': { lat: -25.2744, lng: 133.7751 },
        'AUT': { lat: 47.5162, lng: 14.5501 },
        'AZE': { lat: 40.1431, lng: 47.5769 },
        'BGD': { lat: 23.6850, lng: 90.3563 },
        'BLR': { lat: 53.7098, lng: 27.9534 },
        'BEL': { lat: 50.5039, lng: 4.4699 },
        'BEN': { lat: 9.3077, lng: 2.3158 },
        'BOL': { lat: -16.2902, lng: -63.5887 },
        'BRA': { lat: -14.2350, lng: -51.9253 },
        'BFA': { lat: 12.2383, lng: -1.5616 },
        'BDI': { lat: -3.3731, lng: 29.9189 },
        'KHM': { lat: 12.5657, lng: 104.9910 },
        'CMR': { lat: 7.3697, lng: 12.3547 },
        'CAN': { lat: 56.1304, lng: -106.3468 },
        'CAF': { lat: 6.6111, lng: 20.9394 },
        'TCD': { lat: 15.4542, lng: 18.7322 },
        'CHL': { lat: -35.6751, lng: -71.5430 },
        'CHN': { lat: 35.8617, lng: 104.1954 },
        'COL': { lat: 4.5709, lng: -74.2973 },
        'COD': { lat: -4.0383, lng: 21.7587 },
        'COG': { lat: -0.2280, lng: 15.8277 },
        'CRI': { lat: 9.7489, lng: -83.7534 },
        'CIV': { lat: 7.5400, lng: -5.5471 },
        'CUB': { lat: 21.5218, lng: -77.7812 },
        'CYP': { lat: 35.1264, lng: 33.4299 },
        'CZE': { lat: 49.8175, lng: 15.4730 },
        'DNK': { lat: 56.2639, lng: 9.5018 },
        'DJI': { lat: 11.8251, lng: 42.5903 },
        'DOM': { lat: 18.7357, lng: -70.1627 },
        'ECU': { lat: -1.8312, lng: -78.1834 },
        'EGY': { lat: 26.8206, lng: 30.8025 },
        'SLV': { lat: 13.7942, lng: -88.8965 },
        'ERI': { lat: 15.1794, lng: 39.7823 },
        'EST': { lat: 58.5953, lng: 25.0136 },
        'ETH': { lat: 9.1450, lng: 40.4897 },
        'FIN': { lat: 61.9241, lng: 25.7482 },
        'FRA': { lat: 46.2276, lng: 2.2137 },
        'GAB': { lat: -0.8037, lng: 11.6094 },
        'GEO': { lat: 42.3154, lng: 43.3569 },
        'DEU': { lat: 51.1657, lng: 10.4515 },
        'GHA': { lat: 7.9465, lng: -1.0232 },
        'GRC': { lat: 39.0742, lng: 21.8243 },
        'GTM': { lat: 15.7835, lng: -90.2308 },
        'GIN': { lat: 9.9456, lng: -9.6966 },
        'HTI': { lat: 18.9712, lng: -72.2852 },
        'HND': { lat: 15.1999, lng: -86.2419 },
        'HUN': { lat: 47.1625, lng: 19.5033 },
        'IND': { lat: 20.5937, lng: 78.9629 },
        'IDN': { lat: -0.7893, lng: 113.9213 },
        'IRN': { lat: 32.4279, lng: 53.6880 },
        'IRQ': { lat: 33.2232, lng: 43.6793 },
        'IRL': { lat: 53.4129, lng: -8.2439 },
        'ISR': { lat: 31.0461, lng: 34.8516 },
        'ITA': { lat: 41.8719, lng: 12.5674 },
        'JAM': { lat: 18.1096, lng: -77.2975 },
        'JPN': { lat: 36.2048, lng: 138.2529 },
        'JOR': { lat: 30.5852, lng: 36.2384 },
        'KAZ': { lat: 48.0196, lng: 66.9237 },
        'KEN': { lat: -0.0236, lng: 37.9062 },
        'KOR': { lat: 35.9078, lng: 127.7669 },
        'KWT': { lat: 29.3117, lng: 47.4818 },
        'KGZ': { lat: 41.2044, lng: 74.7661 },
        'LAO': { lat: 19.8563, lng: 102.4955 },
        'LVA': { lat: 56.8796, lng: 24.6032 },
        'LBN': { lat: 33.8547, lng: 35.8623 },
        'LBR': { lat: 6.4281, lng: -9.4295 },
        'LBY': { lat: 26.3351, lng: 17.2283 },
        'LTU': { lat: 55.1694, lng: 23.8813 },
        'MDG': { lat: -18.7669, lng: 46.8691 },
        'MWI': { lat: -13.2543, lng: 34.3015 },
        'MYS': { lat: 4.2105, lng: 101.9758 },
        'MLI': { lat: 17.5707, lng: -3.9962 },
        'MEX': { lat: 23.6345, lng: -102.5528 },
        'MDA': { lat: 47.4116, lng: 28.3699 },
        'MNG': { lat: 46.8625, lng: 103.8467 },
        'MAR': { lat: 31.7917, lng: -7.0926 },
        'MOZ': { lat: -18.6657, lng: 35.5296 },
        'MMR': { lat: 21.9162, lng: 95.9560 },
        'NAM': { lat: -22.9576, lng: 18.4904 },
        'NPL': { lat: 28.3949, lng: 84.1240 },
        'NLD': { lat: 52.1326, lng: 5.2913 },
        'NZL': { lat: -40.9006, lng: 174.8860 },
        'NIC': { lat: 12.8654, lng: -85.2072 },
        'NER': { lat: 17.6078, lng: 8.0817 },
        'NGA': { lat: 9.0820, lng: 8.6753 },
        'NOR': { lat: 60.4720, lng: 8.4689 },
        'OMN': { lat: 21.5126, lng: 55.9233 },
        'PAK': { lat: 30.3753, lng: 69.3451 },
        'PSE': { lat: 31.9522, lng: 35.2332 },
        'PAN': { lat: 8.5380, lng: -80.7821 },
        'PNG': { lat: -6.3150, lng: 143.9555 },
        'PRY': { lat: -23.4425, lng: -58.4438 },
        'PER': { lat: -9.1900, lng: -75.0152 },
        'PHL': { lat: 12.8797, lng: 121.7740 },
        'POL': { lat: 51.9194, lng: 19.1451 },
        'PRT': { lat: 39.3999, lng: -8.2245 },
        'QAT': { lat: 25.3548, lng: 51.1839 },
        'ROU': { lat: 45.9432, lng: 24.9668 },
        'RUS': { lat: 61.5240, lng: 105.3188 },
        'RWA': { lat: -1.9403, lng: 29.8739 },
        'SAU': { lat: 23.8859, lng: 45.0792 },
        'SEN': { lat: 14.4974, lng: -14.4524 },
        'SRB': { lat: 44.0165, lng: 21.0059 },
        'SLE': { lat: 8.4606, lng: -11.7799 },
        'SGP': { lat: 1.3521, lng: 103.8198 },
        'SVK': { lat: 48.6690, lng: 19.6990 },
        'SVN': { lat: 46.1512, lng: 14.9955 },
        'SOM': { lat: 5.1521, lng: 46.1996 },
        'ZAF': { lat: -30.5595, lng: 22.9375 },
        'SSD': { lat: 6.8770, lng: 31.3070 },
        'ESP': { lat: 40.4637, lng: -3.7492 },
        'LKA': { lat: 7.8731, lng: 80.7718 },
        'SDN': { lat: 12.8628, lng: 30.2176 },
        'SWE': { lat: 60.1282, lng: 18.6435 },
        'CHE': { lat: 46.8182, lng: 8.2275 },
        'SYR': { lat: 34.8021, lng: 38.9968 },
        'TWN': { lat: 23.6978, lng: 120.9605 },
        'TJK': { lat: 38.8610, lng: 71.2761 },
        'TZA': { lat: -6.3690, lng: 34.8888 },
        'THA': { lat: 15.8700, lng: 100.9925 },
        'TGO': { lat: 8.6195, lng: 0.8248 },
        'TUN': { lat: 33.8869, lng: 9.5375 },
        'TUR': { lat: 38.9637, lng: 35.2433 },
        'TKM': { lat: 38.9697, lng: 59.5563 },
        'UGA': { lat: 1.3733, lng: 32.2903 },
        'UKR': { lat: 48.3794, lng: 31.1656 },
        'ARE': { lat: 23.4241, lng: 53.8478 },
        'GBR': { lat: 55.3781, lng: -3.4360 },
        'USA': { lat: 37.0902, lng: -95.7129 },
        'URY': { lat: -32.5228, lng: -55.7658 },
        'UZB': { lat: 41.3775, lng: 64.5853 },
        'VEN': { lat: 6.4238, lng: -66.5897 },
        'VNM': { lat: 14.0583, lng: 108.2772 },
        'YEM': { lat: 15.5527, lng: 48.5164 },
        'ZMB': { lat: -13.1339, lng: 27.8493 },
        'ZWE': { lat: -19.0154, lng: 29.1549 },
        'GLB': { lat: 20, lng: 0 }
    };

    const infoWindowRef = useRef<any>(null);
    const radarLayerRef = useRef<any>(null);
    const heatmapLayerRef = useRef<any>(null);
    const activeCircleRef = useRef<any>(null);
    const tacticalPolylinesRef = useRef<google.maps.Polyline[]>([]);
    const tacticalRouteMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

    const [mapReady, setMapReady] = useState(false);
    const [geoJsonLoaded, setGeoJsonLoaded] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGrounding, setIsGrounding] = useState<string | null>(null);

    // Stability: Store callbacks in refs to prevent unnecessary re-initialization
    const onCountryClickRef = useRef(onCountryClick);
    useEffect(() => { onCountryClickRef.current = onCountryClick; }, [onCountryClick]);

    // Helper to log generated reports
    const saveReportMetadata = async (type: string, country: string, title: string, resultData: any) => {
        if (!auth.currentUser?.email) return;
        try {
            await addDoc(collection(db, "generated_reports"), {
                type,
                country,
                title,
                resultData: sanitizeFirestoreData(resultData),
                timestamp: serverTimestamp(),
                userEmail: auth.currentUser.email,
                userName: auth.currentUser.displayName || auth.currentUser.email
            });
        } catch (e) {
            console.error("Error logging report metadata:", e);
        }
    };


    const { assets: globalAssets } = useGlobalAssets();
    const { loadError } = useGoogleMaps();
    
    useImperativeHandle(ref, () => ({
        clearSelection: () => {
            setSelectedEvent(null);
            setIsModalOpen(false);
            if (activeCircleRef.current) activeCircleRef.current.setMap(null);
            activeCircleRef.current = null;
            infoWindowRef.current?.close();
            mapInstanceRef.current?.panTo({ lat: 20, lng: 0 });
            mapInstanceRef.current?.setZoom(2);
        },
        showEventDetail: (event: any) => {
            setSelectedEvent(event);
            setIsModalOpen(true);
            if (mapInstanceRef.current && getMapCoordinates(event)) {
                mapInstanceRef.current.panTo(getMapCoordinates(event));
                mapInstanceRef.current.setZoom(8);
            }
        }
    }));

    // --- STABLE STYLING LOGIC ---
    const applyRiskStyles = useCallback((map: any) => {
        if (!map || !map.data) return;

        const riskLookup = new Map();
        (riskData || []).forEach((d: any) => {
            const code = (d.iso3 || d.id || d.countryCode || d.countryIso3)?.toUpperCase();
            if (code) {
                // Prioritize overallGeopoliticalRiskScore from daily analysis
                const score = getEventRiskScore(d) ?? getEventRiskLabel(d);
                if (score !== undefined && score !== null) {
                    riskLookup.set(code, score);
                }
            }
        });

        // Use a stable style function that doesn't clear during zoom
        map.data.setStyle((feature: any) => {
            let iso = (
                feature.getProperty('iso_a3') || 
                feature.getProperty('ISO_A3') || 
                feature.getProperty('iso3') || 
                feature.getProperty('id')
            )?.toString().toUpperCase();

            if (iso === '-99') {
                iso = (feature.getProperty('adm0_iso') || feature.getProperty('adm0_a3') || feature.getProperty('su_a3'))?.toString().toUpperCase();
            }

            if (!iso) return { visible: false };

            const score = riskLookup.get(iso);
            const hasData = score !== undefined && score !== null;
            const highlighted = iso === highlightIso3;
            const baseColor = hasData ? getRiskHexColor(score) : highlighted ? '#2563EB' : getRiskHexColor(score);

            return {
                fillColor: baseColor,
                fillOpacity: hasData ? 0.75 : highlighted ? 0.45 : 0.25,
                strokeWeight: highlighted ? 2.5 : hasData ? 1.0 : 0.4,
                strokeColor: highlighted ? '#1D4ED8' : hasData ? '#FFFFFF' : '#94A3B8',
                visible: iso !== 'ATA',
                clickable: true 
            };
        });
    }, [riskData, highlightIso3]);

    // --- INITIALIZATION ---
    useEffect(() => {
        if (!isLoaded || !window.google?.maps)
            return;
        let cancelled = false;
        const initMap = async () => {
            if (mapInstanceRef.current) return;
            
            try {
                const { Map } = await (window.google.maps as any).importLibrary("maps");
                await (window.google.maps as any).importLibrary("marker"); 
                await (window.google.maps as any).importLibrary("geometry"); 
                await (window.google.maps as any).importLibrary("visualization");

                if (cancelled || !mapContainerRef.current) return;
                const map = new Map(mapContainerRef.current, {
                    center: { lat: initialViewState?.latitude ?? 20, lng: initialViewState?.longitude ?? 0 },
                    zoom: initialViewState?.zoom ?? 2,
                    mapId: MAP_ID,
                    disableDefaultUI: true,
                    clickableIcons: false,
                    gestureHandling: 'cooperative',
                    backgroundColor: '#0f172a', // Match theme to prevent flickering
                });

                mapInstanceRef.current = map;
                infoWindowRef.current = new window.google.maps.InfoWindow({ maxWidth: 280 });

                if (MAP_ID) {
                    console.log("UnifiedMap: Map initialized with Map ID:", MAP_ID);
                } else {
                    console.warn("UnifiedMap: VITE_GOOGLE_MAPS_MAP_ID is missing. AdvancedMarkerElement may not render.");
                }

                // Event Listeners (Optimization: Handle Tooltips only on mouseover)
                map.data.addListener('mouseover', (event: any) => {
                    map.setOptions({ draggableCursor: 'pointer' });
                    const name = event.feature.getProperty('name') || "Country Info";
                    infoWindowRef.current.setContent(`<div class="p-2 text-slate-800 font-bold">${name}</div>`);
                    infoWindowRef.current.setPosition(event.latLng);
                    infoWindowRef.current.open({ map, shouldFocus: false });
                });

                map.data.addListener('mouseout', () => {
                    map.setOptions({ draggableCursor: 'grab' });
                    infoWindowRef.current.close();
                });

                // --- MAP INTERACTION (CLICK TRAGGERED ONLY) ---
                map.data.addListener('click', (event: any) => {
                    let iso3 = (event.feature.getProperty('iso_a3') || event.feature.getProperty('ISO_A3'))?.toString().toUpperCase();
                    if (iso3 === '-99') {
                        iso3 = (event.feature.getProperty('adm0_iso') || event.feature.getProperty('adm0_a3') || event.feature.getProperty('su_a3'))?.toString().toUpperCase();
                    }
                    if (iso3 && iso3 !== 'ATA' && onCountryClickRef.current) {
                        onCountryClickRef.current(iso3);
                        // Optional: Highlight chosen country
                        mapInstanceRef.current.data.revertStyle();
                        mapInstanceRef.current.data.overrideStyle(event.feature, { strokeWeight: 2, strokeColor: '#3b82f6' });
                    }
                });

                setMapReady(true);
            } catch (err) {
                console.error("Map initialization failed:", err);
            }
        };
        initMap();
        return () => {
            cancelled = true;
            const map = mapInstanceRef.current;
            if (map) {
                window.google.maps.event.clearInstanceListeners(map);
                window.google.maps.event.clearInstanceListeners(map.data);
                map.data.forEach((feature: any) => map.data.remove(feature));
            }
            infoWindowRef.current?.close();
            infoWindowRef.current = null;
            mapInstanceRef.current = null;
            setMapReady(false);
            setGeoJsonLoaded(false);
        };
    }, [isLoaded, initialViewState.latitude, initialViewState.longitude, initialViewState.zoom]);

    useEffect(() => {
        if (mapReady && mapInstanceRef.current && mapContainerRef.current)
            return preserveReportMapPrintView(mapInstanceRef.current, mapContainerRef.current);
    }, [mapReady]);

    // --- DYNAMIC LISTENERS (ZOOM/PAN/RESIZE PERSISTENCE) ---
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !mapReady) return;

        // Use a slightly debounced or delayed approach for zoom events to ensure properties are available
        let redrawTimer: ReturnType<typeof setTimeout>;
        const redraw = () => {
            clearTimeout(redrawTimer);
            redrawTimer = setTimeout(() => applyRiskStyles(map), 50);
        };

        const zoomListener = map.addListener('zoom_changed', redraw);
        const dragListener = map.addListener('dragend', redraw);
        const idleListener = map.addListener('idle', redraw);
        
        // Handle browser window resize (including Ctrl + Mouse Zoom)
        window.addEventListener('resize', redraw);

        return () => {
            window.google.maps.event.removeListener(zoomListener);
            window.google.maps.event.removeListener(dragListener);
            window.google.maps.event.removeListener(idleListener);
            window.removeEventListener('resize', redraw);
            clearTimeout(redrawTimer);
        };
    }, [mapReady, applyRiskStyles]);
    
    // --- FOCUS EFFECT ---
    useEffect(() => {
        if (!mapInstanceRef.current || !mapReady || !focusedLocation) return;
        console.log("UnifiedMap: Applying focusedLocation:", focusedLocation);
        const { lat, lng, zoom = 12 } = focusedLocation;
        mapInstanceRef.current.panTo({ lat, lng });
        mapInstanceRef.current.setZoom(zoom);
    }, [focusedLocation, mapReady]);

    // --- GEOJSON LOADER & REACTIVE STYLES ---
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!mapReady || !choroplethEnabled || !map) return;
        let cancelled = false;

        const loadOrUpdate = async () => {
            if (!geoJsonLoaded) {
                try {
                    const { default: data } = await import('../data/risk-map.json');
                    if (cancelled) return;
                    map.data.addGeoJson(data);
                    setGeoJsonLoaded(true);
                    console.log("✅ GeoJSON loaded and injected.");
                } catch (err) {
                    console.error("GeoJSON load failed:", err);
                }
            }
            
            // Re-apply styles whenever riskData or geoJson changes
            if (!cancelled) {
                applyRiskStyles(map);
            }
        };

        loadOrUpdate();
        return () => { cancelled = true; };
    }, [mapReady, choroplethEnabled, geoJsonLoaded, applyRiskStyles, riskData]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!mapReady || !geoJsonLoaded || !map || !filterIso3 || filterIso3 === 'Global' || focusedLocation)
            return;
        const bounds = new window.google.maps.LatLngBounds();
        map.data.forEach((feature: any) => {
            let code = String(feature.getProperty('iso_a3') || feature.getProperty('ISO_A3') || '').toUpperCase();
            if (code === '-99')
                code = String(feature.getProperty('adm0_iso') || feature.getProperty('adm0_a3') || '').toUpperCase();
            if (code === filterIso3)
                feature.getGeometry()?.forEachLatLng((point: any) => bounds.extend(point));
        });
        if (!bounds.isEmpty())
            map.fitBounds(bounds, 36);
        else if (COUNTRY_COORDINATES[filterIso3]) {
            map.panTo(COUNTRY_COORDINATES[filterIso3]);
            map.setZoom(5);
        }
    }, [mapReady, geoJsonLoaded, filterIso3, focusedLocation, fitCountryRequest]);

    // --- FLYTO EFFECT ---
    useEffect(() => {
        if (focusedLocation && mapInstanceRef.current) {
            mapInstanceRef.current.panTo({ lat: focusedLocation.lat, lng: focusedLocation.lng });
            mapInstanceRef.current.setZoom(focusedLocation.zoom || 8);
            applyRiskStyles(mapInstanceRef.current);

            // Auto-select the event if coordinates match
            const allPoints = [
                ...(alerts || []).map((a: any) => ({ ...a, isAlert: true })), 
                ...(markers || []).map((m: any) => ({ ...m, isCustom: true })),
                ...(assets || []).map((as: any) => ({ ...as, isAsset: true })),
                ...(showGlobalAssets ? globalAssets || [] : []).map((gas: any) => ({ ...gas, isAsset: true }))
            ];

            const match = allPoints.find(p => {
                const pLat = Number(p.lat || p.geo?.lat || p.location?.lat || p.latitude);
                const pLng = Number(p.lng || p.geo?.lng || p.location?.lng || p.longitude);
                // Use a small epsilon for coordinate matching
                return Math.abs(pLat - focusedLocation.lat) < 0.0001 && Math.abs(pLng - focusedLocation.lng) < 0.0001;
            });

            if (match) {
                setSelectedEvent(match);
                setIsModalOpen(true);
            }
        }
    }, [focusedLocation, applyRiskStyles, alerts, markers, assets, globalAssets, showGlobalAssets]);

    const pinScope = countryPinsOnly ? (filterIso3 && filterIso3 !== 'Global' ? filterIso3 : null) : undefined;
    const pinInputs = useMemo(() => {
        const scoped = (items: any[]) => pinScope === undefined ? items : !pinScope ? [] : items.filter(item => eventCountry(item) === pinScope);
        return { alerts: scoped(alerts), markers: scoped([...markers, ...circles, ...geospatialThreats]), assets: scoped(assets),
            globalAssets: showGlobalAssets ? scoped(globalAssets) : [], riskData: scoped(riskData || []) };
    }, [pinScope, alerts, markers, circles, geospatialThreats, assets, globalAssets, riskData, showGlobalAssets]);

    // Only this effect owns its markers. Late geocoding cannot revive old pins.
    useEffect(() => {
        if (!mapReady || !isLoaded) return;
        let cancelled = false;
        const ownedMarkers: any[] = [];
        const isCategoryFiltered = activeCategory && activeCategory !== 'All';
        const { alerts, markers, assets, globalAssets, riskData } = pinInputs;
        const setupMarkers = async () => {
                const { AdvancedMarkerElement, PinElement } = await (window.google.maps as any).importLibrary("marker");
                
                if (cancelled || !mapInstanceRef.current) return;

                const allPoints = [
                    ...(alerts || []).map((a: any) => ({ ...a, isAlert: true })), 
                    ...(markers || []).map((m: any) => ({ ...m, isCustom: true })),
                    ...(assets || []).map((as: any) => ({ ...as, isAsset: true })),
                    ...(globalAssets || []).map((gas: any) => ({ ...gas, isAsset: true })),
                    // Extract from riskData (Batch Results) — suppressed when category filter is active
                    ...(!isCategoryFiltered ? (riskData || []).flatMap((rd: any) => {
                        const staticPos = COUNTRY_COORDINATES[rd.iso3];
                        const lat = Number(rd.lat ?? staticPos?.lat ?? 0);
                        const lng = Number(rd.lng ?? staticPos?.lng ?? 0);

                        return [
                            // 1. The Country Itself (Global Risk Indicator)
                            { ...rd, isCountryRisk: true, title: rd.country || rd.iso3, id: `country-${rd.iso3}`, lat, lng },
                            // 2. Infrastructure
                            ...(Array.isArray(rd.infrastructureMapData) ? rd.infrastructureMapData : []).map((i: any) => ({ ...i, isAsset: true, title: i.name, id: `infra-${rd.iso3}-${i.name}` })),
                            // 3. Regional Risks
                            ...(Array.isArray(rd.regionalRisks) ? rd.regionalRisks : []).map((rr: any) => ({ ...rr, isAlert: true, lat: rr.coordinates?.lat, lng: rr.coordinates?.lng, title: rr.drivers, id: `risk-${rd.iso3}-${rr.drivers}` })),
                            // 4. Embassies
                            ...(Array.isArray(rd.embassies) ? rd.embassies : []).map((e: any) => ({ ...e, isAsset: true, title: 'Embassy/Mission', id: `emb-${rd.iso3}-${e.lat}` })),
                            // 5. Incidents
                            ...(Array.isArray(rd.incidents) ? rd.incidents : []).map((inc: any, idx: number) => ({ ...inc, isIncident: true, title: inc.title || inc.summary, id: `inc-${rd.iso3}-${idx}` })),
                            // 6. Tactical
                            ...(Array.isArray(rd.tactical) ? rd.tactical : []).map((t: any, idx: number) => ({ ...t, isTactical: true, title: t.title || t.summary, id: `tac-${rd.iso3}-${idx}` }))
                        ];
                    }) : [])
                ];
                
                const seenIds = new Set();
                const uniquePoints = allPoints.filter(p => {
                    const coordinates = getMapCoordinates(p);
                    const lat = coordinates?.lat || 0;
                    const lng = coordinates?.lng || 0;
                    // Use title as final fallback for uniqueness to ensure no sidebar items are collapsed
                    const pid = p.id || p.crisis_id || p.summary || p.title || `${lat}-${lng}`;
                    if (seenIds.has(pid)) return false;
                    seenIds.add(pid);
                    return true;
                });

                // Helper for delay
                const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

                    for (const point of (countryPinsOnly && !allCountryPins ? uniquePoints.slice(0, COUNTRY_PIN_LIMIT) : uniquePoints)) {
                    if (cancelled) return;
                    const coordinates = getMapCoordinates(point);
                    let lat = coordinates?.lat || 0;
                    let lng = coordinates?.lng || 0;
                    
                    if (countryPinsOnly && (!coordinates || (lat === 0 && lng === 0))) continue;
                    // --- GEOCODING FALLBACK ---
                    // Don't geocode if valid coordinates are present
                    if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
                        let candidateAddress = point.geo?.location_name || point.location_name || point.name || point.crisis_name || point.title || "";
                        let countryFallback = point.geo?.country_iso3 || point.iso3 || "";

                        // Strip internal markers
                        candidateAddress = candidateAddress.replace(/\[INTEL_PENDING\]/g, '').trim();

                        // Relaxed length check and intelligent fallback
                        const geocoder = new window.google.maps.Geocoder();
                        let resolved = false;

                        if (candidateAddress && candidateAddress.length < 150 && candidateAddress.toLowerCase() !== 'global') {
                            try {
                                const response = await geocoder.geocode({ address: candidateAddress });
                                if (response.results[0]) {
                                    lat = response.results[0].geometry.location.lat();
                                    lng = response.results[0].geometry.location.lng();
                                    resolved = true;
                                }
                            } catch (e) { /* Geocoding failed, try country fallback */ }
                        }

                        // Last ditch effort: Geocode the country itself if specific location failed or was "Global"
                        if (cancelled) return;
                        if (!resolved && countryFallback && countryFallback.length === 3) {
                             try {
                                const response = await geocoder.geocode({ address: countryFallback });
                                if (response.results[0]) {
                                    lat = response.results[0].geometry.location.lat();
                                    lng = response.results[0].geometry.location.lng();
                                    resolved = true;
                                }
                            } catch (e) { /* Final failure */ }
                        }

                        if (!resolved) continue; // Skip if no location found at all
                    }

                    if (cancelled) return;
                    // --- COORDINATE VALIDATION ---
                    if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
                        console.warn(`Skipping marker with invalid coordinates: ${point.title || point.id}`);
                        continue;
                    }

                    // --- DETERMINISTIC JITTER ---
                    // Group markers by country/location to apply spiral offset
                    const offset = getDeterministicOffset(markerInstancesRef.current.length % 20, 0.08);
                    lat += offset.lat;
                    lng += offset.lng;

                    const isThreat = (point.sentiment < 0 || point.analysis?.sentiment < 0 || (getEventRiskScore(point) ?? -1) >= 80 || point.isAlert || /threat|risk|conflict/i.test(point.type || ''));
                    const isAsset = point.isAsset;
                    const isRouteStop = point.type === 'RouteStop';
                    
                    let color = (isThreat || point.isAlert) ? '#EF4444' : '#3b82f6';
                    let emoji = (isThreat || point.isAlert) ? '⚠️' : '🏢';
                    
                    if (point.isCountryRisk) {
                        color = getRiskHexColor(getEventRiskScore(point) ?? getEventRiskLabel(point));
                        emoji = '🌍';
                    }

                    if (point.isTactical || point.type === 'Operational HQ') {
                        color = '#6366f1'; // Indigo
                        emoji = '🛡️';
                    }

                    if (point.isIncident) {
                        color = '#f43f5e'; // Rose
                        emoji = '💥';
                    }
                    
                    if (point.type === 'Medical') {
                        color = '#EF4444';
                        emoji = '🏥';
                    }

                    if (point.type === 'Airport') {
                        color = '#2563EB'; // Blue
                        emoji = '✈️';
                    }

                    if (point.type === 'Border Crossing') {
                        color = '#7C3AED'; // Violet
                        emoji = '🛂';
                    }

                    if (point.type === 'Port') {
                        color = '#0284C7'; // Sky
                        emoji = '🚢';
                    }

                    if (point.type === 'Police Station') {
                        color = '#1E3A8A'; // Navy
                        emoji = '👮';
                    }

                    if (point.type === 'Embassy') {
                        color = '#F59E0B'; // Amber
                        emoji = '🏛️';
                    }

                    if (point.type === 'Evac Point') {
                        color = '#10B981'; // Emerald
                        emoji = '🚁';
                    }
                    
                    if (isAsset && !point.type) {
                        color = point.criticality === 'Critical' ? '#EF4444' : '#60A5FA';
                        emoji = '🏢';
                    }

                    if (point.isPrimary || point.title === 'PROJECT ASSET') {
                        color = '#F59E0B'; // Gold
                        emoji = '⭐';
                    }

                    if (isRouteStop) {
                        color = '#10b981'; 
                        emoji = '📍';
                    }

                    // Use PinElement for industry-standard anchoring and scaling
                    const pin = new PinElement({
                        background: color,
                            borderColor: point.id && point.id === selectedEvent?.id ? '#F59E0B' : '#FFFFFF',
                        glyphText: emoji,
                            scale: point.id && point.id === selectedEvent?.id ? 1.2 : 0.8
                    });

                    const marker = new AdvancedMarkerElement({
                        position: { lat, lng },
                        map: mapInstanceRef.current,
                        title: point.title || point.name || point.id,
                        content: pin, // Fixed: use PinElement directly per deprecation warning
                        zIndex: isThreat ? 100 : 50
                    });

                marker.addListener('gmp-click', () => {
                    
                    setSelectedEvent(point);
                    setIsModalOpen(true);
                    mapInstanceRef.current.panTo({ lat, lng });
                    mapInstanceRef.current.setZoom(10);
                    
                    if (activeCircleRef.current) activeCircleRef.current.setMap(null);
                    activeCircleRef.current = new window.google.maps.Circle({
                        map: mapInstanceRef.current,
                        center: { lat, lng },
                        radius: 25000,
                        fillColor: color,
                        fillOpacity: 0.35,
                        strokeColor: color,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        clickable: false
                    });
                });

                        ownedMarkers.push(marker);
                        markerInstancesRef.current = ownedMarkers;

                        // If we geocoded, wait a bit to avoid hitting rate limits
                        if (!countryPinsOnly && !point.lat && !point.lng && (point.name || point.title)) {
                            await sleep(150);
                        }
                    }

        };
        void setupMarkers().catch(error => { if (!cancelled) console.warn('Map pins could not load:', error); });

        return () => {
            cancelled = true;
            for (const marker of ownedMarkers) {
                window.google.maps.event.clearInstanceListeners(marker);
                marker.map = null;
            }
            ownedMarkers.length = 0;
            if (markerInstancesRef.current === ownedMarkers) markerInstancesRef.current = [];
            if (activeCircleRef.current) activeCircleRef.current.setMap(null);
            activeCircleRef.current = null;
        };
    }, [pinInputs, mapReady, isLoaded, activeCategory, countryPinsOnly, allCountryPins, selectedEvent?.id]);

    // --- ASSETS SYNC (Merged into MARKERS SYNC above) ---
    useEffect(() => {
        // Redundant - markers and assets are handled in the effect above
    }, [globalAssets, mapReady]);

    // --- HEATMAP SYNC ---
    useEffect(() => {
        const localMap = mapInstanceRef.current;
        if (!localMap || !mapReady || !heatmapPoints?.length) {
            if (heatmapLayerRef.current) heatmapLayerRef.current.setMap(null);
            return;
        }
        const data = heatmapPoints.map((p: any) => {
            const latLng = new window.google.maps.LatLng(p.lat, p.lng);
            if (p.weight !== undefined) return { location: latLng, weight: p.weight };
            return latLng;
        });
        if (heatmapLayerRef.current) heatmapLayerRef.current.setMap(null);
        // HeatmapLayer class still exists in v3.65 but throws on instantiation — must use try-catch
        try {
            heatmapLayerRef.current = new window.google.maps.visualization.HeatmapLayer({ data, map: localMap, radius: 50 });
        } catch (e) {
            console.warn('UnifiedMap: HeatmapLayer removed in Maps API v3.65 — heatmap display skipped.', e);
            heatmapLayerRef.current = null;
        }
        return () => heatmapLayerRef.current?.setMap(null);
    }, [heatmapPoints, mapReady]);


    // --- WEATHER SYNC ---
    useEffect(() => {
        const localMap = mapInstanceRef.current;
        if (!localMap || !mapReady || !weatherMode) {
            if (radarLayerRef.current) localMap.overlayMapTypes.clear();
            return;
        }

        const vcApiKey = import.meta.env.VITE_VISUAL_CROSSING_API_KEY;
        if (!vcApiKey) return;

        radarLayerRef.current = new window.google.maps.ImageMapType({
            getTileUrl: (coord: any, zoom: number) => {
                return `https://maps.visualcrossing.com/VisualCrossingWebServices/rest/api/v1/map/tile/precipcomposite/${zoom}/${coord.x}/${coord.y}.png?key=${vcApiKey}&time=latest`;
            },
            tileSize: new window.google.maps.Size(256, 256),
            opacity: 0.5,
        });

        localMap.overlayMapTypes.clear();
        localMap.overlayMapTypes.insertAt(0, radarLayerRef.current);

        return () => localMap.overlayMapTypes.clear();
    }, [weatherMode, mapReady]);
    
    // --- TACTICAL ROUTES SYNC ---
    useEffect(() => {
        const localMap = mapInstanceRef.current;
        if (!localMap || !mapReady || !tacticalRoutes) return;
        let cancelled = false;

        clearRoutePolylines(tacticalPolylinesRef.current);
        tacticalPolylinesRef.current = [];
        for (let index = 0; index < tacticalRouteMarkersRef.current.length; index++)
            tacticalRouteMarkersRef.current[index].map = null;
        tacticalRouteMarkersRef.current = [];

        const routeBounds = new window.google.maps.LatLngBounds();
        let routePointCount = 0;
        for (let routeIndex = 0; routeIndex < tacticalRoutes.length; routeIndex++) {
            const route = tacticalRoutes[routeIndex];
            for (let pointIndex = 0; pointIndex < (route.waypoints || []).length; pointIndex++) {
                const point = route.waypoints[pointIndex];
                const lat = Number(point?.lat);
                const lng = Number(point?.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng))
                    continue;
                routeBounds.extend({ lat, lng });
                routePointCount++;
            }
        }
        if (routePointCount > 1)
            localMap.fitBounds(routeBounds, 60);
        else if (routePointCount === 1) {
            localMap.panTo(routeBounds.getCenter());
            localMap.setZoom(10);
        }

        void Promise.all(tacticalRoutes.map(async route => {
            if (!route.origin || !route.destination) {
                drawFallbackPolyline(route, localMap);
                return;
            }

            try {
                const routes = await computeDrivingRoutes({
                    origin: route.origin,
                    destination: route.destination,
                    intermediates: (route.waypoints || []).map((waypoint: { lat: number; lng: number }) => ({
                        location: { lat: Number(waypoint.lat), lng: Number(waypoint.lng) },
                        via: false
                    }))
                });
                if (cancelled)
                    return;
                if (!routes.length) {
                    drawFallbackPolyline(route, localMap);
                    return;
                }

                const polylines = renderRoutePolylines(routes[0], localMap, {
                    strokeColor: route.color || '#10b981',
                    strokeOpacity: 0.8,
                    strokeWeight: 5
                });
                tacticalPolylinesRef.current.push(...polylines);

                try {
                    const markers = await routes[0].createWaypointAdvancedMarkers({ map: localMap });
                    if (cancelled) {
                        for (let index = 0; index < markers.length; index++)
                            markers[index].map = null;
                        return;
                    }
                    tacticalRouteMarkersRef.current.push(...markers);
                } catch (error) {
                    console.warn('Route markers failed:', error);
                }
            } catch (error) {
                if (!cancelled) {
                    console.warn('Route request failed:', error);
                    drawFallbackPolyline(route, localMap);
                }
            }
        }));

        function drawFallbackPolyline(route: any, map: any) {
            const path = route.waypoints.map((w: { lat: number; lng: number }) => ({ lat: Number(w.lat), lng: Number(w.lng) }));
            const polyline = new window.google.maps.Polyline({
                path,
                geodesic: true,
                strokeColor: route.color || '#10b981',
                strokeOpacity: 0.8,
                strokeWeight: 4,
                map: map,
                icons: route.isDash ? [{
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 },
                    offset: '0',
                    repeat: '20px'
                }] : []
            });
            tacticalPolylinesRef.current.push(polyline);
        }

        return () => {
            cancelled = true;
            clearRoutePolylines(tacticalPolylinesRef.current);
            tacticalPolylinesRef.current = [];
            for (let index = 0; index < tacticalRouteMarkersRef.current.length; index++)
                tacticalRouteMarkersRef.current[index].map = null;
            tacticalRouteMarkersRef.current = [];
        };
    }, [tacticalRoutes, mapReady]);

    useImperativeHandle(ref, () => ({
        clearSelection: () => {
            setSelectedEvent(null);
            setIsModalOpen(false);
            if (activeCircleRef.current) activeCircleRef.current.setMap(null);
            activeCircleRef.current = null;
            infoWindowRef.current?.close();
            mapInstanceRef.current?.panTo({ lat: 20, lng: 0 });
            mapInstanceRef.current?.setZoom(2);
        },
        flyTo: (coords: [number, number], zoomLevel: number = 8) => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.panTo({ lat: coords[1], lng: coords[0] });
                mapInstanceRef.current.setZoom(zoomLevel);
            }
        },
        showEventDetail: (event: any) => {
            setSelectedEvent(event);
            setIsModalOpen(true);
            const lat = Number(event.lat ?? event.geo?.lat ?? event.location?.lat);
            const lng = Number(event.lng ?? event.geo?.lng ?? event.location?.lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                mapInstanceRef.current?.panTo({ lat, lng });
            }
        }
    }));

    if (loadError) return <div className="p-10 text-red-500 bg-slate-950 h-full flex items-center justify-center">Map Engine Error: {loadError.message}</div>;
    if (!isLoaded) return <div className="p-10 text-slate-500 italic bg-slate-950 h-full flex items-center justify-center">Initializing Global Intelligence...</div>;

    const sidebarThreatMarkers = [...markers, ...circles, ...geospatialThreats].filter((marker: any) => {
        const classification = `${marker.type || ''} ${marker.title || ''}`.toLowerCase();
        return /threat|risk|incident|conflict|hazard|unrest|attack/.test(classification);
    }).map((marker: any) => ({
        ...marker,
        iso3: marker.iso3 || (filterIso3 !== 'Global' ? filterIso3 : undefined),
        title: marker.title || marker.name,
        summary: marker.summary || marker.description
    }));
    const sidebarEvents = [...alerts, ...sidebarThreatMarkers.filter((marker: any) => !alerts.some((alert: any) => alert.id && alert.id === marker.id))];

    return (
        <div className="flex w-full h-full bg-slate-950 overflow-hidden relative">
            <main className="relative flex-grow h-full bg-slate-950">
                <div ref={mapContainerRef} className="absolute inset-0 z-0" style={{ cursor: 'crosshair' }} />
            </main>

            {!hideSidebar && (
                <aside className="h-full z-20 flex shrink-0 border-l border-slate-900">
                    <RiskSidebar 
                        events={sidebarEvents} 
                        filterIso3={filterIso3} 
                        title={sidebarTitle}
                        onEventClick={(evt: any) => {
                            setSelectedEvent(evt);
                            setIsModalOpen(true);
                            if (mapInstanceRef.current) {
                                mapInstanceRef.current.panTo({ lat: evt.geo?.lat || evt.lat, lng: evt.geo?.lng || evt.lng });
                                mapInstanceRef.current.setZoom(8);
                            }
                        }}
                        onReset={() => {
                            if (onCountryClick) onCountryClick(null);
                            setSelectedEvent(null);
                            setIsModalOpen(false);
                            if (mapInstanceRef.current) {
                                mapInstanceRef.current.setZoom(2);
                                mapInstanceRef.current.panTo({ lat: 20, lng: 0 });
                            }
                        }} 
                    />
                </aside>
            )}

            {isModalOpen && selectedEvent && (
                <aside className="absolute right-0 top-0 bottom-0 w-[450px] bg-slate-950 border-l border-slate-800 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
                    <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] font-black text-blue-400 tracking-tighter uppercase">TAC-INTEL // {selectedEvent.geo?.country_iso3 || 'GLOBAL'}</span>
                        </div>
                        <button onClick={() => { setIsModalOpen(false); setSelectedEvent(null); }} className="text-slate-500 hover:text-white transition-colors text-2xl">×</button>
                    </div>
                    <div className="p-8 space-y-8 overflow-y-auto flex-grow custom-scrollbar font-sans">
                        <header>
                            <span className="text-[10px] font-mono text-blue-500 font-bold uppercase tracking-[0.2em] mb-2 block">
                                {selectedEvent.category ? `// ${selectedEvent.category.toUpperCase()}` : "RISK ALERT"}
                            </span>
                            <h2 className="text-2xl font-bold text-slate-100 uppercase leading-tight">
                                {selectedEvent.analysis?.intel_title || selectedEvent.title}
                            </h2>
                            {/* NEW: Grounding Status Badge */}
                            {(selectedEvent.vettingStatus === 'published_low_confidence' || selectedEvent.analysis?.incident_summary?.includes("Tactical detail pending")) && (
                                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
                                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                                    <span className="text-[9px] font-black text-amber-500 uppercase tracking-widest">Unverified OSINT · Low confidence</span>
                                </div>
                            )}
                        </header>
                        
                        <div className="space-y-4">
                            <p className="text-slate-300 italic border-l-4 border-blue-600/40 pl-4 text-sm leading-relaxed">
                                {selectedEvent.analysis?.incident_summary || selectedEvent.summary || selectedEvent.content || "Intelligence synthesis in progress for this tactical sector."}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 py-6 border-y border-slate-800/50">
                            <div className="text-center">
                                <span className="block text-[10px] uppercase text-slate-500 mb-1 font-bold tracking-widest">Risk Score</span>
                                <span className="text-2xl font-black text-red-500">{formatEventRiskScore(selectedEvent)}</span>
                                {selectedEvent.scoreMethod === 'qualitative_osint_estimate' && <span className="block text-[10px] text-slate-400">OSINT estimate</span>}
                            </div>
                            <div className="text-center border-l border-slate-800/50">
                                <span className="block text-[10px] uppercase text-slate-500 mb-1 font-bold tracking-widest">Confidence</span>
                                <span className="text-2xl font-black text-emerald-500">
                                    {formatEventConfidenceLevel(selectedEvent)}
                                </span>
                            </div>
                        </div>

                        <p className="text-xs text-slate-400">{selectedEvent.sourceNote || OSINT_EVENT_NOTE}</p>
                        {(selectedEvent.sourceUrl || selectedEvent.source_url) && /^https?:\/\//i.test(selectedEvent.sourceUrl || selectedEvent.source_url) && (
                            <a href={selectedEvent.sourceUrl || selectedEvent.source_url} target="_blank" rel="noopener noreferrer"
                                className="block text-xs text-cyan-400 hover:text-cyan-300">Open OSINT source report</a>
                        )}

                        {/* VERIFICATION STATEMENT */}
                        <section className="space-y-3">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                <TerminologyInline term="Verification" label="Verification Statement" onSelect={onOpenGlossary} />
                            </h4>
                            <p className="text-xs text-slate-400 bg-slate-900/50 p-4 rounded border border-slate-800/50 leading-relaxed italic">
                                {selectedEvent.verification?.statement || selectedEvent.verification?.summary || selectedEvent.verification_statement || "Legacy event record. Review source evidence before operational use."}
                            </p>
                        </section>

                        {Array.isArray(selectedEvent.vettingSources) && selectedEvent.vettingSources.length > 0 && (
                            <section className="space-y-3">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                                    Corroborating Sources
                                </h4>
                                <div className="space-y-2">
                                    {selectedEvent.vettingSources.map((source: any, index: number) => (
                                        <a
                                            key={`${source.publisher || source.title}-${index}`}
                                            href={source.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block text-xs text-cyan-400 hover:text-cyan-300 bg-slate-900/50 p-3 rounded border border-slate-800/50"
                                        >
                                            <span className="block font-bold">{source.publisher || source.title}</span>
                                            <span className="block text-slate-500 mt-1 truncate">{source.title}</span>
                                        </a>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* METHODOLOGY */}
                        <section className="space-y-3">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                                <TerminologyInline term="Pattern Recognition" label="Analytic Methodology" onSelect={onOpenGlossary} />
                            </h4>
                            <p className="text-xs text-slate-400 leading-relaxed">
                                {selectedEvent.riskJustification || selectedEvent.methodology || "No risk assessment methodology was supplied for this item."}
                            </p>
                        </section>

                        {/* CONFIDENCE JUSTIFICATION */}
                        <section className="space-y-3">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                                <TerminologyInline term="Intelligence Cycle" label="Confidence Justification" onSelect={onOpenGlossary} />
                            </h4>
                            <p className="whitespace-pre-line text-xs text-slate-400 leading-relaxed">
                                {selectedEvent.confidenceJustification || selectedEvent.confidence?.justification || selectedEvent.analystConfidence?.justification || "No confidence assessment was supplied for this item."}
                            </p>
                            {selectedEvent.confidence?.factors && (
                                <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                                    {([
                                        ['Source agreement', 'sourceAgreement'],
                                        ['Source quality', 'sourceQuality'],
                                        ['Recency', 'timeliness'],
                                        ['Specificity', 'evidenceSpecificity'],
                                        ['Independent sources', 'sourceIndependence']
                                    ] as const).map(([label, key]) => (
                                        <div key={key} className="flex justify-between gap-2 rounded border border-slate-800 px-2 py-1">
                                            <span>{label}</span>
                                            <span>{selectedEvent.confidence.factors[key]}%</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                        
                        {/* TREND ANALYSIS */}
                        {(selectedEvent.geo?.country_iso3 || selectedEvent.country_iso3 || selectedEvent.iso3) && (
                            <section className="space-y-3">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                                        Risk Trajectory Analysis
                                    </div>
                                    <RiskTrendIcon 
                                        trend={selectedEvent.riskTrend || (selectedEvent.analysis as any)?.risk_trend} 
                                        delta={selectedEvent.riskDelta || (selectedEvent.analysis as any)?.risk_delta} 
                                    />
                                </h4>
                                <RiskTrendChart iso3={selectedEvent.geo?.country_iso3 || selectedEvent.country_iso3 || selectedEvent.iso3} height={150} />
                            </section>
                        )}

                        {/* OPERATIONAL ACTIONS */}

                        {!readOnly && onNavigate && (selectedEvent.geo?.country_iso3 || selectedEvent.country_iso3 || selectedEvent.iso3) && (
                            <section className="space-y-3 pt-6 border-t border-slate-800/50">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                    Operational Actions
                                </h4>
                                <button 
                                    disabled={isGenerating}
                                    onClick={async () => {
                                        const country = selectedEvent.geo?.country_iso3 || selectedEvent.country_iso3 || selectedEvent.iso3;
                                        if (!country) return;
                                        
                                        setIsGenerating(true);
                                        try {
                                            const docRef = doc(db, "daily_country_analysis", country);
                                            const snap = await getDoc(docRef);
                                            if (snap.exists()) {
                                                onNavigate({ 
                                                    page: 'CountryAnalysisResults', 
                                                    params: { 
                                                        reportData: sanitizeFirestoreData(snap.data()),
                                                        country: country,
                                                        isDailyMicro: true 
                                                    } 
                                                });

                                                // Log to generated_reports
                                                await saveReportMetadata('daily_micro', country, `Daily Micro Analysis: ${country}`, snap.data());
                                            } else {
                                                void showAppAlert(`No daily micro report found for ${country}. Please contact Intelligence Operations for on-demand tactical briefs.`);
                                            }
                                        } catch (e) {
                                            console.error("Fetch Error:", e);
                                        } finally {
                                            setIsGenerating(false);
                                        }
                                    }}
                                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-xs uppercase tracking-widest transition-all shadow-lg overflow-hidden relative group active:scale-[0.98] disabled:opacity-50"
                                >
                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                        {isGenerating ? '⌛ Initializing...' : `🚀 Generate Daily Micro Report (${selectedEvent.geo?.country_iso3 || selectedEvent.country_iso3 || selectedEvent.iso3})`}
                                    </span>
                                    {!isGenerating && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />}
                                </button>

                                {/* NEW: GROUNDING BUTTON */}
                                <button 
                                    disabled={isGrounding !== null}
                                    onClick={async () => {
                                        if (!selectedEvent.id) return;
                                        setIsGrounding(selectedEvent.id);
                                        try {
                                            const functions = getFunctions(undefined, 'us-central1');
                                            const reprocess = httpsCallable(functions, 'reprocessEvent');
                                            await reprocess({ eventId: selectedEvent.id });
                                        } catch (err: any) {
                                            void showAppAlert(`Grounding failed: ${err.message}`);
                                        } finally {
                                            setIsGrounding(null);
                                        }
                                    }}
                                    className={`w-full py-2.5 rounded font-bold text-xs uppercase tracking-widest transition-all shadow-lg mt-3 flex items-center justify-center gap-2 ${
                                        selectedEvent.analysis?.incident_summary?.includes("Tactical detail pending") || !selectedEvent.verified
                                        ? "bg-blue-600 hover:bg-blue-500 text-white animate-pulse"
                                        : "bg-slate-900 border border-slate-700 text-slate-400 hover:text-slate-200"
                                    }`}
                                >
                                    {isGrounding === selectedEvent.id ? (
                                        <>
                                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                                            Targeting Ground Truth...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                            </svg>
                                            {selectedEvent.analysis?.incident_summary?.includes("Tactical detail pending") 
                                                ? "Execute High-Fidelity Grounding" 
                                                : "Refresh Tactical Intelligence"
                                            }
                                        </>
                                    )}
                                </button>
                            </section>
                        )}
                    </div>
                    <footer className="p-6 border-t border-slate-800 text-[9px] text-slate-600 font-bold uppercase tracking-widest text-center">
                        Strictly Confidential // Professional Use Only
                    </footer>
                </aside>
            )}
        </div>
    );
});

export default UnifiedMap;
